import React, { useContext, useEffect, useState } from "react";
import { DashboardHeader } from "../../components/DashboardHeader";
import "./RaceSchedule.scss";
import { CurrentRaceEventContext } from "../../context/currentRaceEvent.context";
import {
  ContentProxyRaceDay,
  ContentProxyRaceDayStatus,
  ContentProxyRaceEvent,
} from "../../models/contentProxyModel";
import { useNavigate } from "react-router-dom";
import { FullLoadingIndicator } from "../../components/FullLoadingIndicator/FullLoadingIndicator";
import RaceScheduleWidget from "../../widgets/RaceScheduleWidget";
import { ScheduleFooter } from "../../components/ScehduleFooter";
import SliderComponent from "./ScheduleSlider/SliderComponent";
import { NavigationSliderContext } from "../../context/navigationSlider.context";
import { VideoReplaySyncContext } from "../../context/videoReplaySync.context";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import AdditionalUserDataModal from "../../components/AdditionalUserDataModal";
import {useWindowSize} from "../../hooks/useWindowSize";
import {useInfiniteScroll} from "../../hooks/useInfiniteScroll";

const XL_SCREEN = 1440;
const L_SCREEN = 1290;

export const RaceSchedule = () => {
  const navigate = useNavigate();
  const { currentSlide, handleSelect } = useContext(NavigationSliderContext);
  const {
    switchRaceEvent,
    allRaceEvents,
    isLoading,
    nextEvent,
    selectedRaceDayId,
    broadcasterRegionalConfig,
    fetchFurtherSeasons,
    hasMoreSeasons,
    seasonRequestsPending,
  } = useContext(CurrentRaceEventContext);
  const { switchEventAndResetTime } = useContext(VideoReplaySyncContext);
  const [acceptedPrivacyPolicy] = useLocalStorageState<boolean>(
    "acceptedPrivacyPolicy",
    false
  );
  const [openPrivacyModal, setOpenPrivacyModal] = useState<boolean>(false);
  const [separatedSeasons, setSeparatedSeasons] = useState<object>({});
  const [seasonNumbers, setSeasonNumbers] = useState<string[]>([]);
  const [activeSlide, setActiveSlide] = useState<
    ContentProxyRaceEvent | undefined
  >(undefined);
  const [showWatchButton, setShowWatchButton] = useState<boolean>(true);
  const {width} = useWindowSize();
  const sentinelRef = useInfiniteScroll({
    onLoadMore: fetchFurtherSeasons,
    isLoading: seasonRequestsPending,
    hasMore: hasMoreSeasons,
  });

  useEffect(() => {
    const seasonsObj =
      allRaceEvents?.reduce((accumulator, raceEvent) => {
        if (
          accumulator[raceEvent.eventSeason] &&
          accumulator[raceEvent.eventSeason].races
        ) {
          accumulator[raceEvent.eventSeason].races.push(raceEvent);
        } else accumulator[raceEvent.eventSeason] = { races: [raceEvent] };
        return accumulator;
      }, {}) || {};

    for (const key in seasonsObj) {
      let eventSlide = 0;
      if (key === Object.keys(seasonsObj)[Object.keys(seasonsObj).length - 1])
        eventSlide = seasonsObj[key].races.reduce(
          (
            lastMatch: number | null,
            item: ContentProxyRaceEvent,
            currentIndex: number
          ) => {
            const raceDayWithReplay =
              item.raceDays &&
              item.raceDays.find((raceDay: ContentProxyRaceDay) => {
                return (
                  raceDay.state === ContentProxyRaceDayStatus.ReplayAvailable || raceDay.state === ContentProxyRaceDayStatus.InProgress
                );
              });
            if (raceDayWithReplay) setActiveSlide(item);
            return raceDayWithReplay ? currentIndex : lastMatch;
          },
          null
        );
      let slidesToShow = 6;
      if (width <= XL_SCREEN) slidesToShow = 5;
      if (width < L_SCREEN) slidesToShow = 4;

      seasonsObj[key].initialSlide = eventSlide;
      seasonsObj[key].slidesToShow = seasonsObj[key].races.length > 5 ? slidesToShow : 5;
    }

    setSeparatedSeasons(prev => ({ ...prev, ...seasonsObj }));
    setSeasonNumbers(prev => {
      const newKeys = Object.keys(seasonsObj).reverse();
      const combined = Array.from(new Set([...prev, ...newKeys]));
      return combined;
    });

  }, [allRaceEvents, width]);

  useEffect(() => {
    if (!acceptedPrivacyPolicy && !isLoading) {
      setOpenPrivacyModal(true);
    }
  }, [isLoading, acceptedPrivacyPolicy]);

  const changeRaceEvent = (
    eventId: string,
    raceDay: ContentProxyRaceDay,
    isLiveRaceDay: boolean
  ) => {
    switchRaceEvent(eventId, raceDay.contentfulId, isLiveRaceDay);
    switchEventAndResetTime();
    navigate("/raceManagement");
  };

  useEffect(() => {
    // Note: React Router v6 doesn't have history.listen
    // This functionality would need to be implemented differently
    // For now, we'll comment this out as it requires a different approach
    /*
    const unlisten = history.listen((newLocation, action) => {
      if (action === "POP") {
        // If a "POP" action event occurs, and the race is in Finished state
        // Prevent user from going back
        // Otherwise send user back to the originating location
        if (isRaceFinished) history.go(1);
        else history.go(-1);
      }
    });

    return () => {
      unlisten();
    };
    */
  }, []);

  return (
    <div className="raceScheduleContainer">
      {isLoading && !currentSlide && <FullLoadingIndicator />}
      <div className="headerContainer">
        <DashboardHeader
          eventId={currentSlide?.contentfulId}
          leftContent={<RaceScheduleWidget isInRaceSchedule={true} />}
        />
      </div>
      {allRaceEvents && (
        <SliderComponent
          activeSlide={currentSlide ? currentSlide : activeSlide}
          allRaceEvents={allRaceEvents}
          changeRaceEvent={changeRaceEvent}
          nextEvent={nextEvent}
          currentSlide={currentSlide}
          handleSelect={handleSelect}
          selectedRaceDayId={selectedRaceDayId}
          broadcasterRegionalConfig={broadcasterRegionalConfig}
          separatedSeasons={separatedSeasons}
          seasonNumbers={seasonNumbers}
          switchRaceEvent={switchRaceEvent}
          switchEventAndResetTime={switchEventAndResetTime}
          showWatchButton={showWatchButton}
          setShowWatchButton={setShowWatchButton}
        />
      )}
      <AdditionalUserDataModal />
      <div ref={sentinelRef} style={{ height: '1px', width: '100%' }} />
      <ScheduleFooter />
    </div>
  );
};
